import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

/**
 * Opérations sur titres (fractionnements) — `api/app/routers/operations_titres.py`.
 *
 * Enregistrer l'opération à partir de son avis BRVM suffit : elle s'applique d'elle-même au
 * premier BOC de sa date effective (portefeuilles et ledger des clients, trading IA, watchlists
 * IA, alertes, suivis, notes, prédictions, nombre de titres). La simulation montre ce qui
 * changera ; l'application manuelle n'est qu'un secours.
 */

export interface LigneRevoir {
    table: string;
    id?: number;
    user_id?: number;
    champ?: string;
    motif: string;
}

export interface ApplicationOst {
    id: number;
    applied_at: string | null;
    source: 'AUTO' | 'ADMIN';
    statut: 'APPLIQUEE' | 'A_REVOIR';
    bilan: Record<string, number> | null;
    a_revoir: LigneRevoir[];
    message: string | null;
}

export interface OperationTitres {
    id: number;
    societe_symbole: string | null;
    type_evenement: string;
    date_effective: string | null;
    parite_nouvelles: number | null;
    parite_anciennes: number | null;
    facteur_titres: number | null;
    facteur_cours: number | null;
    avis_url: string | null;
    titre_annonce: string | null;
    en_vigueur: boolean;
    refus: string | null;
    application: ApplicationOst | null;
}

export interface PlanOst {
    symbole: string;
    date_effective: string;
    facteur_titres: number;
    facteur_cours: number;
    bilan: Record<string, number>;
    a_revoir: LigneRevoir[];
}

export interface NouveauFractionnement {
    societe_symbole: string;
    date_effective: string;
    parite_nouvelles: number;
    parite_anciennes: number;
    avis_url: string;
    titre_annonce?: string;
}

/** Résultat d'un appel : `erreur` est un message en français, jamais le texte brut de l'API. */
export interface ResultatAppel<T> {
    ok: boolean;
    data?: T;
    erreur?: string;
}

async function lireErreur(r: Response, defaut: string): Promise<string> {
    // Les messages 4xx de ce routeur sont rédigés en français pour l'admin ; un 5xx ne l'est pas.
    if (r.status >= 500) return defaut;
    try {
        const corps = await r.json();
        return typeof corps.detail === 'string' ? corps.detail : defaut;
    } catch {
        return defaut;
    }
}

export function useOperationsTitres() {
    const { token } = useAuth();
    const [operations, setOperations] = useState<OperationTitres[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const charger = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const r = await fetch(`${API_URL}/v1/admin/operations-titres`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) {
                setError('La liste des opérations sur titres n’a pas pu être chargée.');
                return;
            }
            const corps = await r.json();
            setOperations(corps.data ?? []);
        } catch (err) {
            console.error('Opérations sur titres :', err);
            setError('Erreur réseau. Vérifiez la connexion à l’API.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        charger();
    }, [charger]);

    const enregistrer = useCallback(
        async (op: NouveauFractionnement): Promise<ResultatAppel<OperationTitres>> => {
            if (!token) return { ok: false, erreur: 'Session expirée.' };
            try {
                const r = await fetch(`${API_URL}/v1/admin/operations-titres`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(op),
                });
                if (!r.ok) return { ok: false, erreur: await lireErreur(r, 'L’opération n’a pas été enregistrée.') };
                const corps = await r.json();
                await charger();
                return { ok: true, data: corps.data };
            } catch (err) {
                console.error('Enregistrement opération sur titres :', err);
                return { ok: false, erreur: 'Erreur réseau : l’opération n’a pas été enregistrée.' };
            }
        },
        [token, charger]
    );

    const simuler = useCallback(
        async (id: number): Promise<ResultatAppel<PlanOst>> => {
            if (!token) return { ok: false, erreur: 'Session expirée.' };
            try {
                const r = await fetch(`${API_URL}/v1/admin/operations-titres/${id}/simulation`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!r.ok) return { ok: false, erreur: await lireErreur(r, 'La simulation a échoué.') };
                const corps = await r.json();
                return { ok: true, data: corps.data };
            } catch (err) {
                console.error('Simulation opération sur titres :', err);
                return { ok: false, erreur: 'Erreur réseau : simulation impossible.' };
            }
        },
        [token]
    );

    const appliquer = useCallback(
        async (id: number): Promise<ResultatAppel<PlanOst>> => {
            if (!token) return { ok: false, erreur: 'Session expirée.' };
            try {
                const r = await fetch(`${API_URL}/v1/admin/operations-titres/${id}/appliquer`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!r.ok) return { ok: false, erreur: await lireErreur(r, 'L’application a échoué : rien n’a été modifié.') };
                const corps = await r.json();
                await charger();
                return { ok: true, data: corps.data };
            } catch (err) {
                console.error('Application opération sur titres :', err);
                return { ok: false, erreur: 'Erreur réseau : vérifiez l’état avant de relancer.' };
            }
        },
        [token, charger]
    );

    return { operations, loading, error, recharger: charger, enregistrer, simuler, appliquer };
}
